import SupportTransactionInfo from "screens/__SUPPORT__/transactions/TransactionInfo";
import AdminPermission from "components/admin/AdminPermission";
import { httpUSearchMerchant } from "api/admin/merchant";
import { httpUSearchTransaction } from "api/admin/transaction";
import { httpUSearchUser } from "api/admin/user";
import { httpUSearchTransaction as httpUSearchTransactionS } from "api/support/transaction";
import { httpUSearchUser as httpUSearchUserS } from "api/support/user";
import useGetSubdomain from "hooks/useGetSubdomain";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useAdminAccess from "hooks/useAdminAccess";
import { requiredAdminPermissionsForPath } from "services/adminAccess";
import { adminRoutes, subdomainRoutes } from "routes/routes";
import TransactionInfo from "../../screens/__ADMIN__/transactions/TransactionInfo";
import Spinner from "./Spinner";

interface SearchProps {
  type: "user" | "merchant" | "transaction";
}
const SearchContent: React.FC<SearchProps> = ({ type }) => {
  const [text, setText] = useState("");

  const { subdomain } = useGetSubdomain();

  const isSupport = subdomain === subdomainRoutes.support;
  const isForTransaction = type === "transaction";

  const [status, setStatus] = useState<
    "idle" | "pending" | "success" | "error"
  >("idle");
  const [value, setValue] = useState<any>(null);

  useEffect(() => {
    const query = text.trim();
    setValue(null);
    setStatus("idle");
    if (query.length < 3) return;

    const controller = new AbortController();
    let active = true;
    const timer = setTimeout(async () => {
      setStatus("pending");
      try {
        const search = isSupport
          ? type === "transaction"
            ? httpUSearchTransactionS
            : httpUSearchUserS
          : type === "merchant"
            ? httpUSearchMerchant
            : type === "transaction"
              ? httpUSearchTransaction
              : httpUSearchUser;
        const result = await search(query, controller.signal);
        if (active) {
          setValue(result);
          setStatus("success");
        }
      } catch {
        if (active) setStatus("error");
      }
    }, 400);

    return () => {
      active = false;
      clearTimeout(timer);
      controller.abort();
    };
  }, [text, type, isSupport]);

  const placeholder = () => {
    if (type === "merchant") return `Search Merchant`;
    if (isForTransaction) return `Search ID`;
    return `Search User`;
  };

  const nameToShow = () => {
    const tr = value?.transaction;
    if (type === "merchant") return `${value?.merchant?.userName}`;
    if (isForTransaction)
      return isSupport ? (
        <SupportTransactionInfo full transaction={tr} />
      ) : (
        <TransactionInfo full transaction={tr} />
      );

    return `${value?.user?.firstName} ${value?.user?.lastName}`;
  };

  const link = () => {
    if (type === "merchant")
      return `${adminRoutes.merchants}/${value?.merchant?.id}/analytics`;
    if (isForTransaction) return `#`;
    return subdomain === subdomainRoutes.admin
      ? `${adminRoutes.users}/${value?.user.id}`
      : `${adminRoutes.balances}/user/${value?.user.id}`;
  };

  return (
    <div className="relative">
      <input
        className="w-full px-10 py-3 bg-gray-200 rounded-lg bg-opacity-70 placeholder:text-gray-400 pl-14"
        placeholder={placeholder()}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <i className="absolute text-gray-400 fa fa-search top-4 left-5"></i>
      {status === "pending" && <Spinner className="absolute top-4 right-5" />}

      {value?.success ? (
        <div className="absolute z-20 w-full px-5 py-8 bg-white rounded shadow-2xl">
          {isForTransaction ? (
            <div>{nameToShow()}</div>
          ) : subdomain === subdomainRoutes.admin ? (
            <AdminSearchResult to={link()} label={nameToShow()} />
          ) : (
            <Link to={link()}>
              <div>{nameToShow()}</div>
            </Link>
          )}
        </div>
      ) : null}
    </div>
  );
};

const AdminSearchResult = ({
  to,
  label,
}: {
  to: string;
  label: React.ReactNode;
}) => {
  const { canAny } = useAdminAccess();
  const content = <div>{label}</div>;
  return canAny(requiredAdminPermissionsForPath(to) ?? []) ? (
    <Link to={to}>{content}</Link>
  ) : (
    content
  );
};

const Search: React.FC<SearchProps> = (props) => {
  const { subdomain } = useGetSubdomain();
  if (subdomain !== subdomainRoutes.admin) return <SearchContent {...props} />;
  const permission =
    props.type === "user"
      ? "users.profile.view"
      : props.type === "merchant"
        ? "merchants.directory.view"
        : "transactions.search.view";
  return (
    <AdminPermission permission={permission}>
      <SearchContent {...props} />
    </AdminPermission>
  );
};

export default Search;
